import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Cube, Download, Plus, Trash, CheckCircle, WarningCircle, XCircle, UploadSimple, Users, SignOut, ClockCounterClockwise, ClipboardText, Storefront, PaperPlaneTilt, Key, BookOpen, SealCheck, ShieldCheck, Gear, UserSwitch, Images, Stack, FileText, CaretUp, FileArrowUp } from '@phosphor-icons/react';
import './App.css';
import BundleBuilder from './pages/BundleBuilder';
import LoginPage from './pages/LoginPage';
import UsersPage from './pages/UsersPage';
import SavedOrdersPage from './pages/SavedOrdersPage';
import SubmittedOrdersPage from './pages/SubmittedOrdersPage';
import raiseysLogo from './assets/raiseys-logo.png';
import TestPlanPage from './pages/TestPlanPage';
import HowToUsePage from './pages/HowToUsePage';
import BrowseOrderPage from './pages/BrowseOrderPage';
import AuditLogPage from './pages/AuditLogPage';
import SettingsPage from './pages/SettingsPage';
import ProductImagesPage from './pages/ProductImagesPage';
import PackAssistantPage from './pages/PackAssistantPage';
import PoImportPage from './pages/PoImportPage';
import CustomerModeModal from './components/CustomerModeModal';
import CustomerConfidentialityModal from './components/CustomerConfidentialityModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import { AuthProvider, useAuth } from './auth/AuthContext';
import AuthCallback from './auth/AuthCallback';
import ProtectedRoute from './auth/ProtectedRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// First SKU from a product's flavourCodes map. Used to point admin thumbnails
// at `/product-images/by-sku/{sku}/file` so uploading an image to the Product
// Images page immediately wires it to the matching product row / form preview.
// Falls back through: flavour-specific SKU → single-entry (flavourless) → null.
const firstSkuOf = (product) => {
  const codes = product?.flavourCodes || {};
  const values = Object.values(codes).filter(Boolean);
  return values.length > 0 ? values[0] : null;
};

// Send cookies on every API call (auth uses httpOnly cookies).
axios.defaults.withCredentials = true;

// ------------------------------------------------------------------
// Sidebar navigation model.
//
// Items are split into two groups on purpose:
//   1. `CUSTOMER_NAV` — visible to everyone (customer, sales_rep, admin).
//      These are the pages a customer can hit, listed first so the sidebar
//      feels the same for a customer whether or not they log in.
//   2. `STAFF_NAV` — admin/sales-rep only. Rendered under a delineated
//      "Staff tools" section so it's obvious which pages a customer never
//      sees. Some entries are gated further via `roles`.
// ------------------------------------------------------------------
const CUSTOMER_NAV = [
  { path: '/browse', label: 'Browse & Order', icon: <Storefront size={20} weight="duotone" /> },
  { path: '/', label: 'Smart Saver', icon: <Package size={20} weight="duotone" /> },
  { path: '/saved-orders', label: 'Saved Orders', icon: <ClockCounterClockwise size={20} weight="duotone" /> },
  { path: '/submitted-orders', label: 'Submitted Orders', icon: <PaperPlaneTilt size={20} weight="duotone" /> },
  { path: '/product-images', label: 'Product Images', icon: <Images size={20} weight="duotone" /> },
  { path: '/how-to-use', label: 'Smart Saver Overview', icon: <BookOpen size={20} weight="duotone" /> },
];

const STAFF_NAV = [
  { path: '/pack-assistant', label: 'Pack Assistant', icon: <Stack size={20} weight="duotone" />, roles: ['admin', 'sales_rep'] },
  { path: '/import-po', label: 'Import Customer PO', icon: <FileArrowUp size={20} weight="duotone" />, roles: ['admin', 'sales_rep'] },
  { path: '/admin', label: 'Product Management', icon: <Cube size={20} weight="duotone" />, roles: ['admin', 'sales_rep'] },
  { path: '/users', label: 'User Management', icon: <Users size={20} weight="duotone" />, roles: ['admin', 'sales_rep'] },
  { path: '/settings', label: 'Settings', icon: <Gear size={20} weight="duotone" />, roles: ['admin'] },
  { path: '/audit-log', label: 'Audit Log', icon: <ShieldCheck size={20} weight="duotone" />, roles: ['admin', 'sales_rep'] },
  { path: '/test-plan', label: 'Test Plan', icon: <ClipboardText size={20} weight="duotone" />, roles: ['admin', 'sales_rep'] },
];

const STAFF_ROLES = new Set(['admin', 'sales_rep']);
const ROLE_LABELS = { admin: 'Admin', sales_rep: 'Sales Rep', customer: 'Customer' };

/**
 * Fetches the certification metadata from /api/certifications and renders a
 * small "Raisey's Quality Assurance" block in the sidebar with one link per
 * uploaded certification (slots with no PDF are hidden so customers don't see
 * dead links). Stays invisible entirely when no certs are uploaded.
 */
const SidebarCertifications = () => {
  const [certs, setCerts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/certifications`)
      .then((res) => { if (!cancelled) setCerts(res.data?.items || []); })
      .catch(() => { /* silently hide the block if the endpoint errors */ });
    return () => { cancelled = true; };
  }, []);
  const visible = certs.filter((c) => c.present);
  if (visible.length === 0) return null;
  return (
    <div className="px-4 pb-1" data-testid="sidebar-certifications">
      <p className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">
        Quality Assurance
      </p>
      <ul className="space-y-0">
        {visible.map((c) => (
          <li key={c.key}>
            <a
              href={`${BACKEND_URL}/api/certifications/${c.key}/file`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`sidebar-cert-${c.key}`}
              className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-gold underline decoration-dotted underline-offset-2 leading-tight py-0.5"
            >
              <SealCheck
                size={12}
                weight="fill"
                className="text-success shrink-0"
                aria-label="Verified certification"
              />
              <span>{c.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const role = user?.role || 'customer';
  const isStaff = STAFF_ROLES.has(role);
  const customerNav = (role === 'customer' && user?.smartSaverEnabled === false)
    ? CUSTOMER_NAV.filter((it) => it.path !== '/' && it.path !== '/how-to-use')
    : CUSTOMER_NAV;
  const staffNav = STAFF_NAV.filter((it) => it.roles.includes(role));
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [customerModeOpen, setCustomerModeOpen] = useState(false);
  // Compact user pill at the bottom of the sidebar. Click to expand the full
  // card with name/email/role details and action buttons (Customer mode,
  // Change password, Sign out). Collapsed state persists across page loads
  // via localStorage so power users can keep it out of the way.
  const [userExpanded, setUserExpanded] = useState(() => {
    try {
      return localStorage.getItem('sidebar.userExpanded') === '1';
    } catch { return false; }
  });
  const toggleUser = () => {
    setUserExpanded((v) => {
      const next = !v;
      try { localStorage.setItem('sidebar.userExpanded', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  const onSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const renderNavItem = (item) => (
    <li key={item.path}>
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-sm ${
          location.pathname === item.path
            ? 'bg-gold text-black font-semibold'
            : 'text-text-primary hover:bg-surface-hover'
        }`}
      >
        {item.icon}
        <span>{item.label}</span>
      </Link>
    </li>
  );

  // Initials shown in the compact user pill (max two chars). Fall back to
  // the first character of the email if `name` is missing.
  const initials = (user?.name || user?.email || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';

  return (
    <div className="w-64 bg-surface border-r border-border-color h-screen sticky top-0 flex flex-col overflow-y-auto print:hidden" data-testid="app-sidebar">
      <div className="p-4 border-b border-border-color shrink-0">
        <div className="flex items-center gap-2.5">
          <img
            src={raiseysLogo}
            alt="Raisey's logo"
            className="h-10 w-auto shrink-0"
            data-testid="sidebar-logo"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-bold font-heading text-gold leading-tight">Raisey&apos;s Smart Saver</h1>
            <p className="text-[10px] text-text-secondary mt-0.5">Build Smart, Save Smart</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-4 pt-3">
        <ul className="space-y-1" data-testid="sidebar-customer-nav">
          {customerNav.map(renderNavItem)}
        </ul>
        <div className="mt-3 mb-1">
          <SidebarCertifications />
          <div className="px-4 pt-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/terms/file`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sidebar-terms-link"
              className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-gold underline decoration-dotted underline-offset-2"
            >
              <FileText
                size={12}
                weight="fill"
                className="text-gold/70 shrink-0"
                aria-hidden
              />
              <span>Terms of Trade</span>
            </a>
          </div>
        </div>

        {isStaff && staffNav.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border-color" data-testid="sidebar-staff-nav">
            <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1.5 px-1">
              Staff tools
            </p>
            <ul className="space-y-1">
              {staffNav.map(renderNavItem)}
            </ul>
          </div>
        )}
        <div className="h-3" />
      </nav>
      {user?.id && (
        <div className="border-t border-border-color" data-testid="sidebar-user-card">
          {/* Compact pill — always visible. Click to expand full card. */}
          <button
            onClick={toggleUser}
            data-testid="sidebar-user-toggle"
            aria-expanded={userExpanded}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-surface-hover text-left"
          >
            <span
              className="shrink-0 h-8 w-8 rounded-full bg-gold text-black text-xs font-bold flex items-center justify-center"
              aria-hidden
            >
              {initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold text-text-primary truncate" title={user.name}>{user.name}</span>
              <span className="block text-[10px] text-gold uppercase tracking-wider">{ROLE_LABELS[user.role] || user.role}</span>
            </span>
            <CaretUp
              size={14}
              weight="bold"
              className={`text-text-secondary shrink-0 transition-transform ${userExpanded ? '' : 'rotate-180'}`}
              aria-hidden
            />
          </button>
          {userExpanded && (
            <div className="px-4 pb-3">
              <p className="text-[10px] text-text-secondary truncate mb-2" title={user.email}>{user.email}</p>
              {user.role === 'admin' && (
                <button
                  onClick={() => setCustomerModeOpen(true)}
                  data-testid="sidebar-customer-mode-button"
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 border border-border-color text-text-secondary rounded-lg text-xs hover:border-gold hover:text-gold"
                >
                  <UserSwitch size={12} weight="bold" /> Customer mode
                </button>
              )}
              <button
                onClick={() => setChangePwOpen(true)}
                data-testid="sidebar-change-password-button"
                className="mt-1.5 w-full flex items-center justify-center gap-2 px-3 py-1.5 border border-border-color text-text-secondary rounded-lg text-xs hover:border-gold hover:text-gold"
              >
                <Key size={12} weight="bold" /> Change password
              </button>
              <button
                onClick={onSignOut}
                data-testid="sidebar-signout-button"
                className="mt-1.5 w-full flex items-center justify-center gap-2 px-3 py-1.5 border border-border-color text-text-secondary rounded-lg text-xs hover:border-gold hover:text-gold"
              >
                <SignOut size={12} weight="bold" /> Sign out
              </button>
            </div>
          )}
        </div>
      )}
      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
      <CustomerModeModal open={customerModeOpen} onClose={() => setCustomerModeOpen(false)} />
    </div>
  );
};

// Admin Product Management Page
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [flavourInput, setFlavourInput] = useState('');
  const [productToDelete, setProductToDelete] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('merge');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [templateModal, setTemplateModal] = useState(null); // { csv }
  const [templateCopyStatus, setTemplateCopyStatus] = useState('idle');
  const [formData, setFormData] = useState({
    name: '',
    range: 'Core',
    size: '',
    wholesalePrice: '',
    rrp: '',
    active: true,
    flavours: [],
    flavourCodes: {},
    category: '',
    imageUrl: '',
    description: '',
    isBundleable: true,
    sizeLabel: 'Tub Saver',
  });
  // Free-text filter that matches name / range / category / size case-insensitively.
  const [search, setSearch] = useState('');
  // SKU-health widget state — top-of-page banner showing how many active
  // products still have no SKU. Zero missing means the banner hides itself.
  const [skuHealth, setSkuHealth] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
    // Refresh the SKU-health count alongside — cheap and keeps the banner in
    // sync after every add/edit/import.
    try {
      const h = await axios.get(`${API}/products/sku-health`);
      setSkuHealth(h.data);
    } catch (e) {
      setSkuHealth(null);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/products/template`, { responseType: 'text' });
      setTemplateModal({ csv: res.data });
    } catch (e) {
      console.error('Failed to load template:', e);
      alert('Could not load template.');
    }
  };

  const downloadTemplateFile = () => {
    if (!templateModal) return;
    const blob = new Blob([templateModal.csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'products_template.csv';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Release the blob URL once the browser has captured the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const copyTemplate = async () => {
    if (!templateModal) return;
    try {
      await navigator.clipboard.writeText(templateModal.csv);
      setTemplateCopyStatus('copied');
      setTimeout(() => setTemplateCopyStatus('idle'), 2000);
    } catch (e) {
      const ta = document.getElementById('template-csv-textarea');
      if (ta) ta.select();
      setTemplateCopyStatus('select');
      setTimeout(() => setTemplateCopyStatus('idle'), 3000);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(
        `${API}/products/import?mode=${importMode}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(res.data);
      fetchProducts();
    } catch (e) {
      console.error('Import failed:', e);
      setImportResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
    setImporting(false);
  };

  // (Old duplicate fetchProducts removed — superseded by useCallback version above.)

  const handleSubmit = async (e) => {
    e.preventDefault();

    // SKU foundation guard: every product must carry at least one non-empty
    // SKU. For single-variant products the code goes into flavourCodes[""];
    // multi-flavour products need at least one flavour + code populated.
    const codes = formData.flavourCodes || {};
    const hasAnySku = Object.values(codes).some((c) => (c || '').trim() !== '');
    if (!hasAnySku) {
      alert(formData.flavours.length === 0
        ? 'Enter an SKU for this product — it needs to match the Unleashed ProductCode.'
        : 'Add at least one Product Code (SKU) to a flavour — required so Unleashed and Pack Assistant can find the product.');
      return;
    }

    const rrpVal = parseFloat(formData.rrp);
    const payload = {
      ...formData,
      // Wholesale is now derived (RRP × 0.75 default). We still write a snapshot value
      // so legacy code that reads product.wholesalePrice doesn't break.
      wholesalePrice: Number.isFinite(rrpVal) ? +(rrpVal * 0.75).toFixed(2) : 0,
      rrp: rrpVal,
      size: formData.size || null,
      // Empty sizeLabel is a valid choice — the product will simply render
      // its size (e.g. "1kg") with no suffix on the Smart Saver card.
      sizeLabel: (formData.sizeLabel || '').trim(),
      category: (formData.category || '').trim() || null,
      imageUrl: (formData.imageUrl || '').trim() || null,
      description: (formData.description || '').trim() || null,
      // Drop empty/whitespace-only codes and codes for flavours that were removed.
      // Preserve the empty-string key `""` — that's the convention for
      // single-variant (flavourless) products' SKU.
      flavourCodes: Object.fromEntries(
        Object.entries(formData.flavourCodes || {})
          .filter(([f, c]) => {
            const codeOk = (c || '').trim() !== '';
            const keyOk = f === '' ? formData.flavours.length === 0 : formData.flavours.includes(f);
            return codeOk && keyOk;
          })
          .map(([f, c]) => [f, c.trim()])
      ),
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
      } else {
        await axios.post(`${API}/products`, payload);
      }
      
      setShowAddForm(false);
      setEditingProduct(null);
      setFlavourInput('');
      setFormData({
        name: '',
        range: 'Core',
        size: '',
        wholesalePrice: '',
        rrp: '',
        active: true,
        flavours: [],
        flavourCodes: {},
        category: '',
        imageUrl: '',
        description: '',
        isBundleable: true,
        sizeLabel: 'Tub Saver',
      });
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFlavourInput('');
    setFormData({
      name: product.name,
      range: product.range,
      size: product.size || '',
      wholesalePrice: product.wholesalePrice.toString(),
      rrp: product.rrp.toString(),
      active: product.active,
      flavours: product.flavours || [],
      flavourCodes: product.flavourCodes || {},
      category: product.category || '',
      imageUrl: product.imageUrl || '',
      description: product.description || '',
      isBundleable: product.isBundleable !== false,
      sizeLabel: product.sizeLabel ?? 'Tub Saver',
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setProductToDelete(null);
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, {
        active: !product.active,
      });
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-2" data-testid="admin-page-title">Product Management</h1>
            <p className="text-text-secondary">Manage your product catalog</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/products/export`, { responseType: 'blob' });
                  // Pull filename from Content-Disposition if present, else fall back to a default.
                  const disp = res.headers['content-disposition'] || '';
                  const match = disp.match(/filename="?([^"]+)"?/i);
                  const filename = match ? match[1] : `products_export_${new Date().toISOString().slice(0,10)}.csv`;
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  a.rel = 'noopener';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (e) {
                  console.error('Export failed:', e);
                  alert(e?.response?.data?.detail || 'Export failed');
                }
              }}
              data-testid="export-products-csv-button"
              className="flex items-center gap-2 px-5 py-3 border border-border-color text-text-primary font-semibold rounded-lg hover:border-gold hover:text-gold"
              title="Download the full catalogue as CSV in the per-flavour import format"
            >
              <Download size={20} weight="bold" />
              Export CSV
            </button>
            <button
              onClick={() => setImportModalOpen(true)}
              data-testid="open-import-modal-button"
              className="flex items-center gap-2 px-5 py-3 border border-gold text-gold font-semibold rounded-lg hover:bg-gold hover:text-black"
            >
              <UploadSimple size={20} weight="bold" />
              Import CSV
            </button>
            <button
              onClick={() => {
                setShowAddForm(!showAddForm);
                setEditingProduct(null);
                setFlavourInput('');
                setFormData({
                  name: '',
                  range: 'Core',
                  size: '',
                  wholesalePrice: '',
                  rrp: '',
                  active: true,
                  flavours: [],
                  flavourCodes: {},
                });
              }}
              data-testid="add-product-button"
              className="flex items-center gap-2 px-6 py-3 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
            >
              <Plus size={20} weight="bold" />
              Add Product
            </button>
          </div>
        </div>

        {skuHealth && skuHealth.missingCount > 0 && (
          <div
            className="mb-6 px-4 py-3 bg-danger/10 border border-danger/40 rounded-lg flex items-start gap-3"
            data-testid="sku-health-banner"
          >
            <WarningCircle size={20} weight="bold" className="text-danger shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-danger">
                {skuHealth.missingCount} product{skuHealth.missingCount === 1 ? '' : 's'} missing an SKU
              </p>
              <p className="text-xs text-text-secondary mt-0.5">
                These products won&apos;t export to Unleashed and will be skipped by the Pack Assistant. Edit each one and set an SKU that matches its Unleashed ProductCode:{' '}
                <span className="text-danger font-mono">
                  {skuHealth.missing.map((m) => `${m.name}${m.size ? ` (${m.size})` : ''}`).join(', ')}
                </span>
              </p>
            </div>
          </div>
        )}

        {showAddForm && (
          <div className="bg-surface border border-border-color rounded-xl p-6 mb-8" data-testid="product-form">
            <h2 className="text-xl font-bold font-heading text-gold mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Product Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="product-name-input"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Range</label>
                  <select
                    value={formData.range}
                    onChange={(e) => {
                      const newRange = e.target.value;
                      // Auto-tick "Show in Smart Saver" when the chosen range is a Saver range.
                      const SAVER_RANGES = new Set(['Core', 'Goal', 'Performance', 'Essentials']);
                      setFormData({ ...formData, range: newRange, isBundleable: SAVER_RANGES.has(newRange) });
                    }}
                    data-testid="product-range-select"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  >
                    <option value="Core">Core</option>
                    <option value="Goal">Goal</option>
                    <option value="Performance">Performance</option>
                    <option value="Essentials">Essentials</option>
                    <option value="Catalogue">Catalogue (non-saver)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">
                    Size <span className="text-text-secondary/60 font-normal">(weight / volume)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    data-testid="product-size-input"
                    placeholder="e.g. 1kg, 900g, 300ml"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary placeholder:text-text-secondary/40 focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">
                    Size label suffix{' '}
                    <span className="text-text-secondary/60 font-normal">
                      (shown after the size on the Smart Saver page, e.g. &quot;Tub Saver&quot;)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.sizeLabel}
                    onChange={(e) => setFormData({ ...formData, sizeLabel: e.target.value })}
                    data-testid="product-size-label-input"
                    placeholder="Tub Saver"
                    maxLength={40}
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary placeholder:text-text-secondary/40 focus:border-gold focus:outline-none"
                  />
                  <p className="text-[10px] text-text-secondary/70 mt-1 italic" data-testid="product-size-label-hint">
                    Leave blank for no suffix — only the size will be shown.
                  </p>
                  {formData.size && (
                    <p className="text-[10px] text-text-secondary mt-1">
                      Preview: <span className="text-gold">{formData.size}{formData.sizeLabel ? ` ${formData.sizeLabel}` : ''}</span>
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">RRP ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.rrp}
                    onChange={(e) => setFormData({ ...formData, rrp: e.target.value })}
                    data-testid="product-rrp-input"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                  <p className="text-[11px] text-text-secondary mt-1 italic">
                    Wholesale is computed per customer (RRP − their override %, or 25% default).
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      data-testid="product-active-checkbox"
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-text-secondary">Active</span>
                  </label>
                </div>
              </div>

              {/* Catalogue extensions — used by the wider ordering shell. All optional. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border-color">
                <div className="md:col-span-2">
                  <label className="text-sm text-text-secondary mb-2 block">Category (catalogue grouping)</label>
                  <input
                    type="text"
                    list="product-category-options"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="product-category-input"
                    placeholder="e.g. Protein, Pre-Workout, Recovery"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                  <datalist id="product-category-options">
                    {Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort().map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-text-secondary mb-2 block">Product image</label>
                  {(() => {
                    const sku = firstSkuOf(formData);
                    const src = sku ? `${API}/product-images/by-sku/${encodeURIComponent(sku)}/file` : null;
                    return src ? (
                      <div className="flex items-center gap-3 mb-2">
                        <img
                          src={src}
                          alt="preview"
                          data-testid="product-image-preview-by-sku"
                          className="h-24 w-24 object-cover rounded border border-border-color"
                          onError={(e) => { e.target.style.display = 'none';
                            const hint = e.target.parentElement?.querySelector('[data-testid="product-image-missing-hint"]');
                            if (hint) hint.style.display = 'block';
                          }}
                        />
                        <p className="text-xs text-text-secondary">
                          Auto-matched to <span className="font-mono text-gold">{sku}</span>. Upload
                          <span className="font-mono text-gold"> {sku}.webp</span> on the
                          <span className="text-gold"> Product Images</span> page to change it.
                        </p>
                        <p data-testid="product-image-missing-hint" className="text-xs text-text-secondary" style={{ display: 'none' }}>
                          No image uploaded yet for <span className="font-mono text-gold">{sku}</span>.
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-text-secondary mb-2">
                        Save the product with an SKU first, then upload
                        <span className="font-mono text-gold"> {'{SKU}'}.webp</span> on the
                        <span className="text-gold"> Product Images</span> page and it will appear here automatically.
                      </p>
                    );
                  })()}
                  <label className="text-xs text-text-secondary mb-1 block">Image URL <span className="text-text-secondary/60">(legacy override — only used if no matching Product Image is uploaded)</span></label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    data-testid="product-image-url-input"
                    placeholder="https://… (optional)"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-text-secondary mb-2 block">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="product-description-input"
                    rows={2}
                    placeholder="Short marketing copy shown on the customer catalogue page…"
                    className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none resize-none"
                  />
                </div>
                <div className="md:col-span-2 flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isBundleable}
                      onChange={(e) => setFormData({ ...formData, isBundleable: e.target.checked })}
                      data-testid="product-is-bundleable-checkbox"
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-text-secondary">Show in Smart Saver (uncheck for catalogue-only items)</span>
                  </label>
                </div>
              </div>

              {/* Flavours Management */}
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Flavours (optional)</label>
                {/* Single-variant SKU input — shown when the product has no
                    flavours. Writes to flavourCodes[""] so the backend and
                    Unleashed integration always have a code to work with. */}
                {formData.flavours.length === 0 && (
                  <div className="mb-3 p-3 border border-gold/30 bg-gold/5 rounded-lg" data-testid="single-variant-sku-panel">
                    <label className="text-xs text-gold font-semibold mb-1 block">Product SKU <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      value={formData.flavourCodes[''] || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        flavourCodes: { ...formData.flavourCodes, '': e.target.value.toUpperCase() },
                      })}
                      data-testid="single-variant-sku-input"
                      placeholder="e.g. TAB-IMMUNT-60C — must match Unleashed ProductCode"
                      className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none font-mono"
                    />
                    <p className="text-[10px] text-text-secondary mt-1">
                      Required. Every product needs an SKU so orders, pack plans and inventory exports resolve correctly. Add flavours below if this product has multiple variants.
                    </p>
                  </div>
                )}
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={flavourInput}
                    onChange={(e) => setFlavourInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = flavourInput.trim();
                        if (v && !formData.flavours.includes(v)) {
                          setFormData({ ...formData, flavours: [...formData.flavours, v] });
                          setFlavourInput('');
                        }
                      }
                    }}
                    placeholder="Type a flavour and press Enter or click Add"
                    data-testid="flavour-input"
                    className="flex-1 px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const v = flavourInput.trim();
                      if (v && !formData.flavours.includes(v)) {
                        setFormData({ ...formData, flavours: [...formData.flavours, v] });
                        setFlavourInput('');
                      }
                    }}
                    data-testid="add-flavour-button"
                    className="px-4 py-2 border border-gold text-gold rounded-lg hover:bg-gold hover:text-black font-semibold"
                  >
                    Add
                  </button>
                </div>
                {formData.flavours.length > 0 && (
                  <div className="space-y-2 mt-2" data-testid="flavour-chips">
                    <p className="text-xs text-text-secondary/70">Add an optional Product Code per flavour.</p>
                    {formData.flavours.map((flavour) => (
                      <div
                        key={flavour}
                        className="flex items-center gap-2 px-3 py-2 bg-gold/5 border border-gold/20 rounded-lg"
                        data-testid={`flavour-row-${flavour}`}
                      >
                        <span className="text-xs text-gold font-medium min-w-[140px] truncate" title={flavour}>{flavour}</span>
                        <input
                          type="text"
                          value={formData.flavourCodes[flavour] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            flavourCodes: { ...formData.flavourCodes, [flavour]: e.target.value },
                          })}
                          data-testid={`flavour-code-${flavour}`}
                          placeholder="Flavour Product Code (optional)"
                          className="flex-1 px-2 py-1 bg-black border border-border-color rounded text-text-primary text-xs focus:border-gold focus:outline-none font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const nextCodes = { ...formData.flavourCodes };
                            delete nextCodes[flavour];
                            setFormData({
                              ...formData,
                              flavours: formData.flavours.filter(f => f !== flavour),
                              flavourCodes: nextCodes,
                            });
                          }}
                          data-testid={`remove-flavour-${flavour}`}
                          className="text-text-secondary hover:text-danger px-2"
                          aria-label={`Remove ${flavour}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  data-testid="submit-product-button"
                  className="px-6 py-2 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
                >
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                  }}
                  data-testid="cancel-product-button"
                  className="px-6 py-2 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-surface border border-border-color rounded-xl overflow-hidden">
          {/* Search bar — case-insensitive across name / range / category / size / sku. */}
          <div className="px-6 py-3 border-b border-border-color">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="product-search-input"
              placeholder="Search products by name, range, category, size…"
              className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary placeholder:text-text-secondary focus:border-gold focus:outline-none text-sm"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead className="bg-black border-b border-border-color">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Range</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Flavours</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">RRP</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .filter((p) => {
                    const q = search.trim().toLowerCase();
                    if (!q) return true;
                    return [p.name, p.range, p.category, p.size]
                      .map((v) => String(v || '').toLowerCase())
                      .some((v) => v.includes(q));
                  })
                  .map((product) => {
                  const rangeLabel = product.range || '—';
                  return (
                  <tr key={product.id} className="border-b border-border-color hover:bg-surface-hover" data-testid={`product-row-${product.id}`}>
                    <td className="px-6 py-4 text-text-primary">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const sku = firstSkuOf(product);
                          const src = sku ? `${API}/product-images/by-sku/${encodeURIComponent(sku)}/file` : product.imageUrl;
                          return src ? (
                            <img
                              src={src}
                              alt=""
                              data-testid={`product-thumb-${product.id}`}
                              className="h-10 w-10 object-cover rounded border border-border-color shrink-0"
                              onError={(e) => {
                                // If SKU-based image is missing, try the legacy imageUrl
                                if (sku && product.imageUrl && !e.target.src.includes(product.imageUrl)) {
                                  e.target.src = product.imageUrl;
                                } else {
                                  e.target.style.display = 'none';
                                }
                              }}
                            />
                          ) : null;
                        })()}
                        <div className="min-w-0">
                          <div>
                            {product.name}
                            {product.size && (
                              <span className="text-text-secondary text-xs ml-2">
                                ({product.size}{product.sizeLabel ? ` ${product.sizeLabel}` : ''})
                              </span>
                            )}
                          </div>
                          {product.isBundleable === false && (
                            <span className="inline-block mt-1 px-1.5 py-0.5 bg-text-secondary/10 text-text-secondary text-[10px] rounded">
                              Catalogue only
                            </span>
                          )}
                          {(() => {
                            // Show a big red warning when a product has no SKU set. This
                            // is the single reason Unleashed and Pack Assistant silently
                            // skip a line — surface it up-front instead of at export time.
                            const codes = product.flavourCodes || {};
                            const hasAny = Object.values(codes).some((c) => (c || '').trim());
                            if (hasAny) return null;
                            return (
                              <span
                                data-testid={`product-missing-sku-${product.id}`}
                                className="inline-flex items-center gap-1 mt-1 ml-1 px-1.5 py-0.5 bg-danger/15 border border-danger/40 text-danger text-[10px] rounded font-bold"
                                title="This product has no SKU — Unleashed, packing and inventory exports will skip it."
                              >
                                <WarningCircle size={10} weight="bold" /> SKU MISSING
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-gold/10 text-gold text-xs rounded">{rangeLabel}</span>
                    </td>
                    <td className="px-6 py-4 text-text-secondary text-sm">
                      {product.category || <span className="text-text-secondary/50">—</span>}
                    </td>
                    <td className="px-6 py-4 text-text-secondary text-sm">
                      {product.flavours && product.flavours.length > 0
                        ? <span className="px-2 py-1 bg-surface-hover rounded text-xs">{product.flavours.length} flavour{product.flavours.length > 1 ? 's' : ''}</span>
                        : <span className="text-text-secondary/50">-</span>}
                    </td>
                    <td className="px-6 py-4 text-text-primary">${product.rrp.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleActive(product)}
                        data-testid={`toggle-active-${product.id}`}
                        className={`px-3 py-1 rounded text-xs font-semibold ${
                          product.active
                            ? 'bg-success/10 text-success'
                            : 'bg-text-secondary/10 text-text-secondary'
                        }`}
                      >
                        {product.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="px-3 py-1 text-sm text-gold hover:text-gold-hover"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setProductToDelete(product)}
                          data-testid={`delete-product-${product.id}`}
                          className="px-3 py-1 text-sm text-danger hover:text-danger/80"
                        >
                          <Trash size={16} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CSV Template Modal */}
      {templateModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => { setTemplateModal(null); setTemplateCopyStatus('idle'); }}
          data-testid="template-modal-overlay"
        >
          <div
            className="bg-surface border border-border-color rounded-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-xl font-bold font-heading text-text-primary">Products CSV Template</h3>
                <p className="text-sm text-text-secondary mt-1">
                  File: <span className="text-gold">products_template.csv</span>
                </p>
              </div>
              <button
                onClick={() => { setTemplateModal(null); setTemplateCopyStatus('idle'); }}
                data-testid="close-template-modal"
                className="text-text-secondary hover:text-text-primary text-2xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <textarea
              id="template-csv-textarea"
              readOnly
              value={templateModal.csv}
              data-testid="template-csv-textarea"
              className="flex-1 min-h-[260px] my-4 p-3 bg-black border border-border-color rounded-lg text-text-primary text-xs font-mono focus:border-gold focus:outline-none resize-none"
              onClick={(e) => e.target.select()}
            />
            <div className="flex flex-wrap gap-3 justify-end items-center">
              {templateCopyStatus === 'copied' && (
                <span className="text-sm text-success" data-testid="template-copy-status">Copied to clipboard</span>
              )}
              {templateCopyStatus === 'select' && (
                <span className="text-sm text-warning" data-testid="template-copy-status">Press Ctrl/Cmd+C to copy</span>
              )}
              <button
                onClick={copyTemplate}
                data-testid="copy-template-button"
                className="px-4 py-2 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
              >
                Copy CSV
              </button>
              <button
                onClick={downloadTemplateFile}
                data-testid="download-template-file-button"
                className="px-4 py-2 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
              >
                Download .csv
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {importModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closeImportModal}
          data-testid="import-modal-overlay"
        >
          <div
            className="bg-surface border border-border-color rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold font-heading text-text-primary">Import Products from CSV</h3>
                <p className="text-sm text-text-secondary mt-1">
                  Bulk upload products. Download the template, fill it in, then upload.
                </p>
              </div>
              <button
                onClick={closeImportModal}
                data-testid="close-import-modal"
                className="text-text-secondary hover:text-text-primary text-2xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="bg-black/40 border border-border-color rounded-lg p-4 mb-4">
              <p className="text-sm text-text-primary mb-2 font-semibold">CSV columns (one row per flavour):</p>
              <ul className="text-xs text-text-secondary space-y-1 list-disc list-inside">
                <li><span className="text-gold">SKU</span> — per-flavour SKU, e.g. <code>PRO-VLA-1000K</code> (required when <code>flavour</code> is set)</li>
                <li><span className="text-gold">name</span> — product family name, e.g. <code>PRO75</code></li>
                <li><span className="text-gold">Subtitle</span> — short descriptor, e.g. <code>Whey Concentrate</code> (becomes the product description)</li>
                <li><span className="text-gold">category</span> — optional grouping tag for Browse &amp; Order, e.g. <code>Protein</code>, <code>Fat Burner</code>, <code>Recovery</code>. Blank → grouped as <code>Other</code>.</li>
                <li><span className="text-gold">range</span> — one of <code>Core</code>, <code>Goal</code>, <code>Performance</code>, <code>Essentials</code>, <code>Catalogue</code>. Blank defaults to <code>Catalogue</code>.</li>
                <li><span className="text-gold">size</span> — e.g. <code>1kg</code>, <code>900g</code>. Rows with the same <code>(name, size)</code> are grouped into one product.</li>
                <li><span className="text-gold">rrp</span> — number, must be {'> 0'} (wholesale is computed per customer; no longer stored on the CSV)</li>
                <li><span className="text-gold">active</span> — <code>TRUE</code>/<code>FALSE</code> (defaults to TRUE)</li>
                <li><span className="text-gold">flavour</span> — single flavour name (leave blank for no-flavour products like Creatine)</li>
                <li><span className="text-gold">isBundleable</span> — optional <code>TRUE</code>/<code>FALSE</code>. Defaults to TRUE for Core/Goal/Performance/Essentials, FALSE for Catalogue.</li>
                <li><span className="text-gold">imageUrl</span> — optional. Public URL to a product photo (shown on Browse &amp; Order cards). Leave blank if you don&apos;t have one.</li>
              </ul>
              <button
                onClick={downloadTemplate}
                data-testid="download-template-button"
                className="mt-3 inline-flex items-center gap-2 text-sm text-gold hover:text-gold-hover font-semibold"
              >
                <Download size={16} weight="bold" /> Download Template
              </button>
            </div>

            <div className="mb-4">
              <label className="text-sm text-text-secondary mb-2 block">Import Mode</label>
              <div className="flex flex-wrap gap-2">
                <label className={`flex-1 min-w-[140px] flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${importMode === 'merge' ? 'border-gold bg-gold/10 text-gold' : 'border-border-color text-text-secondary'}`}>
                  <input
                    type="radio"
                    name="import-mode"
                    value="merge"
                    checked={importMode === 'merge'}
                    onChange={() => setImportMode('merge')}
                    data-testid="import-mode-merge"
                  />
                  <div>
                    <div className="text-sm font-semibold">Merge</div>
                    <div className="text-xs">Update if name+size exists, otherwise add</div>
                  </div>
                </label>
                <label className={`flex-1 min-w-[140px] flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${importMode === 'replace' ? 'border-danger bg-danger/10 text-danger' : 'border-border-color text-text-secondary'}`}>
                  <input
                    type="radio"
                    name="import-mode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                    data-testid="import-mode-replace"
                  />
                  <div>
                    <div className="text-sm font-semibold">Replace All</div>
                    <div className="text-xs">Delete all products then insert CSV rows</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm text-text-secondary mb-2 block">CSV File</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                data-testid="import-file-input"
                className="block w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gold file:text-black file:font-semibold file:cursor-pointer hover:file:bg-gold-hover"
              />
              {importFile && (
                <p className="text-xs text-text-secondary mt-2">Selected: <span className="text-gold">{importFile.name}</span> ({Math.round(importFile.size / 1024)} KB)</p>
              )}
            </div>

            {importResult && (
              <div className="bg-black/40 border border-border-color rounded-lg p-4 mb-4" data-testid="import-result">
                {importResult.error ? (
                  <p className="text-sm text-danger">Error: {importResult.error}</p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-success">{importResult.inserted}</div>
                        <div className="text-xs text-text-secondary">Inserted</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gold">{importResult.updated}</div>
                        <div className="text-xs text-text-secondary">Updated</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-warning">{importResult.skipped}</div>
                        <div className="text-xs text-text-secondary">Skipped</div>
                      </div>
                    </div>
                    {(importResult.products_grouped !== undefined || importResult.rows_parsed !== undefined) && (
                      <p className="text-[11px] text-text-secondary text-center mb-2" data-testid="import-grouping-summary">
                        {importResult.rows_parsed} row(s) parsed → grouped into {importResult.products_grouped} product(s)
                      </p>
                    )}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <details className="text-xs text-text-secondary">
                        <summary className="cursor-pointer text-warning">{importResult.errors.length} message(s) — click to view</summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside max-h-32 overflow-y-auto">
                          {importResult.errors.map((err, i) => <li key={`${i}-${err}`}>{err}</li>)}
                        </ul>
                      </details>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeImportModal}
                data-testid="cancel-import-button"
                className="px-4 py-2 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
              >
                {importResult ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                data-testid="upload-import-button"
                className="px-4 py-2 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? 'Uploading...' : 'Upload & Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {productToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setProductToDelete(null)}
          data-testid="delete-confirm-overlay"
        >
          <div
            className="bg-surface border border-border-color rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold font-heading text-text-primary mb-2">Delete product?</h3>
            <p className="text-sm text-text-secondary mb-6">
              This will permanently remove <span className="text-gold font-semibold">{productToDelete.name}</span>
              {productToDelete.size && ` (${productToDelete.size}${productToDelete.sizeLabel ? ` ${productToDelete.sizeLabel}` : ''})`} from the catalog. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setProductToDelete(null)}
                data-testid="cancel-delete-button"
                className="px-4 py-2 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(productToDelete.id)}
                data-testid="confirm-delete-button"
                className="px-4 py-2 bg-danger text-white font-semibold rounded-lg hover:bg-danger/90"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Slim floating bar shown ONLY while an admin is inside customer mode — the
// single trace of admin identity in an otherwise 100% real customer session.
const ImpersonationBanner = () => {
  const { user, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!user?.impersonating) return null;
  const exit = async () => {
    setBusy(true);
    try {
      await stopImpersonation();
      navigate('/users');
    } catch (e) {
      console.error('Exit customer mode failed:', e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed top-0 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-4 py-2 bg-amber-400 text-black rounded-b-lg shadow-xl print:hidden"
      data-testid="impersonation-banner"
    >
      <UserSwitch size={16} weight="bold" />
      <span className="text-xs font-semibold">Customer mode — signed in as {user.name}</span>
      <button
        onClick={exit}
        disabled={busy}
        data-testid="impersonation-exit-button"
        className="text-xs font-bold underline underline-offset-2 disabled:opacity-50 whitespace-nowrap"
      >
        {busy ? 'Returning…' : 'Return to admin'}
      </button>
    </div>
  );
};

const NotFoundPage = () => (
  <div className="flex-1 p-8 flex items-center justify-center" data-testid="not-found-page">
    <div className="text-center max-w-md">
      <h1 className="text-4xl font-bold font-heading text-text-primary mb-2">Page not found</h1>
      <p className="text-text-secondary mb-6">That link doesn&apos;t exist — it may have moved or been mistyped.</p>
      <Link
        to="/"
        data-testid="not-found-home-link"
        className="inline-block px-5 py-2.5 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
      >
        Back to Smart Saver
      </Link>
    </div>
  </div>
);

// Main App Component
const AppRouter = () => {
  const location = useLocation();
  // Detect Emergent Google OAuth callback whenever the hash contains session_id=.
  // We MUST consume useLocation so this re-evaluates on route changes (e.g., after
  // AuthCallback navigates to "/"); otherwise the callback screen stays mounted.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <div className="flex">
              <Sidebar />
              <ImpersonationBanner />
              <CustomerConfidentialityModal />
              <Routes>
                <Route path="/" element={<BundleBuilder />} />
                <Route path="/browse" element={<BrowseOrderPage />} />
                <Route path="/saved-orders" element={<SavedOrdersPage />} />
                <Route path="/submitted-orders" element={<SubmittedOrdersPage />} />
                <Route path="/product-images" element={<ProductImagesPage />} />
                <Route
                  path="/pack-assistant"
                  element={
                    <ProtectedRoute roles={["admin", "sales_rep"]}>
                      <PackAssistantPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import-po"
                  element={
                    <ProtectedRoute roles={["admin", "sales_rep"]}>
                      <PoImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/test-plan" element={<TestPlanPage />} />
                <Route path="/how-to-use" element={<HowToUsePage />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute roles={["admin", "sales_rep"]}>
                      <AdminProducts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={["admin", "sales_rep"]}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/audit-log"
                  element={
                    <ProtectedRoute roles={["admin", "sales_rep"]}>
                      <AuditLogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
